import { ReactNode, useMemo } from 'react';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import { AppBar, AppBarProps, Toolbar, Tooltip, useMediaQuery } from '@mui/material';

// project import
import AppBarStyled from './AppBarStyled';
import HeaderContent from './HeaderContent';
import IconButton from 'components/@extended/IconButton';

// assets
import { sizeByBreakpoint } from '../../../utils/helper';

// The sidebar-panel glyph (the pattern Linear/Notion use): a panel outline
// with a chevron that flips toward the action. One glyph whose inside
// animates beats two arrow icons swapping.
const SidebarToggleIcon = ({ open }: { open: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="2.5" y="3.5" width="15" height="13" rx="3" stroke="currentColor" strokeWidth="1.5" />
    <line x1="7.75" y1="3.5" x2="7.75" y2="16.5" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M13.75 8 L11.75 10 L13.75 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      style={{
        transformOrigin: '12.75px 10px',
        transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
        transition: 'transform 280ms cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    />
  </svg>
);

// ==============================|| MAIN LAYOUT - HEADER ||============================== //

interface Props {
  open: boolean;
  handleDrawerToggle?: () => void;
}

const Header = ({ open, handleDrawerToggle }: Props) => {
  const theme = useTheme();
  const matchDownMD = useMediaQuery(theme.breakpoints.down('lg'));

  // header content
  const headerContent = useMemo(() => <HeaderContent />, []);

  // const iconBackColorOpen = theme.palette.mode === 'dark' ? 'grey.200' : 'grey.300';
  // const iconBackColor = theme.palette.mode === 'dark' ? 'background.default' : 'grey.100';

  // common header
  const mainHeader: ReactNode = (
    <Toolbar
      sx={{
        minHeight: 60,
        height: 60,
        px: '0 !important',
        '@media (min-width: 768px)': { paddingLeft: 0, paddingRight: 0 },
        borderBottom: '1px solid',
        borderColor: (theme) => theme.palette.border.main
      }}
    >
      <Tooltip title={open ? 'Collapse sidebar' : 'Expand sidebar'} placement="bottom">
        <IconButton
          aria-label={open ? 'collapse sidebar' : 'expand sidebar'}
          onClick={handleDrawerToggle}
          color="primary"
          sx={{
            width: sizeByBreakpoint(1.5, 36),
            height: sizeByBreakpoint(1.5, 36),
            borderColor: 'transparent !important',
            borderRadius: '10px',
            // Ghost button, like the other header controls: no permanent fill —
            // the old always-on teal block read as a stray highlight. State
            // lives in the glyph (chevron direction), not in a background.
            color: theme.palette.text.secondary,
            bgcolor: 'transparent',
            ml: 0,
            transition: 'background-color 280ms cubic-bezier(0.4, 0, 0.2, 1), color 280ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              color: theme.palette.primary.main,
              bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.06) : alpha(theme.palette.primary.main, 0.08)
            }
          }}
        >
          <SidebarToggleIcon open={open} />
        </IconButton>
      </Tooltip>
      {headerContent}
    </Toolbar>
  );

  // app-bar params
  const appBar: AppBarProps = {
    position: 'fixed',
    color: 'inherit',
    elevation: 0,
    sx: {
      px: 4,
      [theme.breakpoints.down('md')]: {
        px: 2
      }
    }
  };

  return (
    <>
      {!matchDownMD ? (
        <AppBarStyled open={open} {...appBar}>
          {mainHeader}
        </AppBarStyled>
      ) : (
        <AppBar {...appBar}>{mainHeader}</AppBar>
      )}
    </>
  );
};

export default Header;
